// ============================================================================
// AI SERVICE - Cloudflare Worker Proxy (SECURE)
// ============================================================================
// This service calls a Cloudflare Worker that holds the OpenAI API key.
// NO API KEY IS EVER SENT TO THE BROWSER - completely secure!
//
// Setup:
// 1. Deploy the worker from /cloudflare-worker/worker.js to Cloudflare
// 2. Set REACT_APP_AI_WORKER_URL in your .env file
// ============================================================================

// Worker URL - set this in your .env file
const WORKER_URL = process.env.REACT_APP_AI_WORKER_URL || '';

// Check if worker is configured
const isWorkerConfigured = () => {
  return WORKER_URL && WORKER_URL.length > 0 && !WORKER_URL.includes('your-worker');
};

// Log configuration status on load
if (isWorkerConfigured()) {
  console.info('🤖 AI Service: Connected to Cloudflare Worker (secure mode)');
} else {
  console.warn('⚠️ AI Service: Worker URL not configured. Using demo mode.');
  console.info('   Set REACT_APP_AI_WORKER_URL in your .env file');
}

// API call helper
const callWorker = async (endpoint, body) => {
  if (!isWorkerConfigured()) {
    throw new Error('Worker not configured');
  }

  const response = await fetch(`${WORKER_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Worker request failed');
  }

  return data.data;
};

// ============================================================================
// MOCK DATA GENERATORS (Fallback when worker unavailable)
// ============================================================================

const generateMockTemplate = (prompt) => ({
  name: `Template: ${prompt.substring(0, 20)}...`,
  description: `AI generated template based on: ${prompt}`,
  type: 'custom',
  sections: [
    {
      id: 'mock-section-1',
      name: 'Professional Competencies',
      order: 1,
      competencies: [
        {
          id: 'comp-1',
          name: 'Communication Skills',
          description: 'Ability to convey ideas effectively.',
          weight: 50,
          scale: { type: 'numeric', min: 0, max: 5 },
          feedbackThreshold: 1,
          allowManualInput: true
        },
        {
          id: 'comp-2',
          name: 'Technical Proficiency',
          description: 'Demonstrates necessary job skills.',
          weight: 50,
          scale: { type: 'numeric', min: 0, max: 5 },
          feedbackThreshold: 1,
          allowManualInput: true
        }
      ]
    }
  ],
  settings: { weightingEnabled: true, notifications: { enabled: true, daysBefore: [30, 14, 7] } }
});

const generateMockAnalysis = (dataStr) => {
  const isPositive = dataStr.toLowerCase().includes('good') || dataStr.toLowerCase().includes('excellent');
  return {
    sentiment: isPositive ? "excellent" : "needs_improvement",
    title: isPositive ? "High Performer Analysis" : "Performance Gap Analysis",
    summary: "Demo analysis: The employee shows strong potential with focused development opportunities.",
    keyInsights: [
      "Consistently meets core deadlines",
      "Strong team collaboration observed",
      "Technical documentation could be improved"
    ],
    strengths: [
      { name: "Collaboration", desc: "Works well with cross-functional teams." },
      { name: "Reliability", desc: "Delivers tasks on time." }
    ],
    areasForGrowth: [
      { name: "Leadership", desc: "Could take more ownership of projects." },
      { name: "Communication", desc: "Ensure updates are timely." }
    ],
    actionPlan: [
      "Attend leadership workshop",
      "Lead one team meeting per month",
      "Update documentation weekly"
    ]
  };
};

const generateMockPayrollAnalysis = (data) => ({
  summary: {
    totalPayroll: data.totalPayroll || 0,
    averagePay: data.averagePay || 0,
    trend: "stable"
  },
  anomalies: [],
  optimizations: [
    {
      title: "Review Overtime Distribution",
      description: "Consider workload balancing to reduce overtime costs.",
      potentialSavings: 500,
      priority: "medium"
    }
  ],
  compliance: [],
  forecast: {
    nextMonth: data.totalPayroll || 0,
    yearEnd: (data.totalPayroll || 0) * 12,
    recommendation: "Maintain current payroll budget with 5% contingency."
  }
});

// ============================================================================
// AI SERVICE EXPORTS
// ============================================================================

export const aiService = {
  /**
   * Check if AI service is available
   */
  isConfigured: isWorkerConfigured,
  
  /**
   * Get current mode
   */
  getMode: () => isWorkerConfigured() ? 'cloudflare-worker' : 'demo',

  /**
   * Generate a template based on a user prompt.
   */
  generateTemplate: async (prompt) => {
    try {
      return await callWorker('/template', { prompt });
    } catch (error) {
      console.warn("Template generation fallback:", error.message);
      return generateMockTemplate(prompt);
    }
  },

  /**
   * Chat with the AI bot.
   */
  chatWithBot: async (history, userRole) => {
    try {
      return await callWorker('/chat', { history, userRole });
    } catch (error) {
      console.warn("Chat fallback:", error.message);
      const lastMsg = history[history.length - 1]?.text || 'your question';
      return `I understand you're asking about "${lastMsg}". As a ${userRole}, you can find relevant information in your dashboard. (AI service temporarily unavailable)`;
    }
  },

  /**
   * Correct or improve text (grammar, tone, etc.).
   */
  correctText: async (text) => {
    try {
      return await callWorker('/correct', { text });
    } catch (error) {
      console.warn("Text correction fallback:", error.message);
      return text; // Return original text if service unavailable
    }
  },

  /**
   * Analyze data and provide insights.
   */
  analyzeData: async (data) => {
    try {
      return await callWorker('/analyze', { data });
    } catch (error) {
      console.warn("Data analysis fallback:", error.message);
      const dataStr = typeof data === 'string' ? data : JSON.stringify(data);
      return generateMockAnalysis(dataStr);
    }
  },

  /**
   * Analyze payroll data and provide insights.
   */
  analyzePayroll: async (payrollData) => {
    try {
      return await callWorker('/payroll-analyze', { payrollData });
    } catch (error) {
      console.warn("Payroll analysis fallback:", error.message);
      return generateMockPayrollAnalysis(payrollData);
    }
  },

  /**
   * Generate natural language payroll summary.
   */
  generatePayrollSummary: async (payrollData) => {
    try {
      return await callWorker('/payroll-summary', { payrollData });
    } catch (error) {
      console.warn("Payroll summary fallback:", error.message);
      return `Payroll processed for ${payrollData.employeeCount || 'N/A'} employees. Total: ${payrollData.total || 'N/A'}. Review any flagged items before final submission.`;
    }
  },

  /**
   * Health check - verify worker is responding
   */
  healthCheck: async () => {
    try {
      const result = await callWorker('/health', {});
      return { healthy: true, ...result };
    } catch (error) {
      return { healthy: false, error: error.message };
    }
  }
};
